import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, ArrowRight, CheckCircle2, Tag } from "lucide-react";
import { useState } from "react";
import AdminPageLayout from "./AdminPageLayout";
import { apiPost } from "./api";

export default function TagsManagement() {
  const [sourceTag, setSourceTag] = useState("");
  const [targetTag, setTargetTag] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const { toast } = useToast();

  const handleMerge = async () => {
    if (!sourceTag.trim() || !targetTag.trim()) {
      toast({
        title: "Validation Error",
        description: "Both source and target tags are required",
        variant: "destructive",
      });
      return;
    }

    try {
      await apiPost("/api/admin/tags/merge", {
        from: sourceTag.trim(),
        to: targetTag.trim(),
      });

      setStatus("success");
      setMessage(`Successfully merged '${sourceTag}' → '${targetTag}'`);

      toast({
        title: "Tags Merged",
        description: `All instances of '${sourceTag}' have been replaced with '${targetTag}'`,
      });

      // Reset form
      setTimeout(() => {
        setSourceTag("");
        setTargetTag("");
        setStatus("idle");
        setMessage("");
      }, 3000);
    } catch (err: any) {
      setStatus("error");
      setMessage(err.message);

      toast({
        title: "Merge Failed",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  return (
    <AdminPageLayout
      title="Tags Management"
      description="Merge duplicate or similar tags across all content"
      icon={<Tag className="w-8 h-8" />}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Merge Tags
          </CardTitle>
          <CardDescription>
            Combine all instances of one tag into another. This will update all
            articles, market listings, and other content using the source tag.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="source-tag">Source Tag (to be replaced)</Label>
              <Input
                id="source-tag"
                placeholder="e.g., 시드니부동산"
                value={sourceTag}
                onChange={(e) => setSourceTag(e.target.value)}
                data-testid="input-source-tag"
              />
              <p className="text-xs text-muted-foreground">
                This tag will be replaced everywhere
              </p>
            </div>

            <div className="flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>

            <div className="space-y-2 md:col-start-2">
              <Label htmlFor="target-tag">Target Tag (keep this)</Label>
              <Input
                id="target-tag"
                placeholder="e.g., 부동산"
                value={targetTag}
                onChange={(e) => setTargetTag(e.target.value)}
                data-testid="input-target-tag"
              />
              <p className="text-xs text-muted-foreground">
                All instances will use this tag
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleMerge}
              disabled={!sourceTag.trim() || !targetTag.trim()}
              data-testid="button-merge-tags"
            >
              <Tag className="mr-2 w-4 h-4" />
              Merge Tags
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setSourceTag("");
                setTargetTag("");
                setStatus("idle");
                setMessage("");
              }}
              data-testid="button-reset"
            >
              Reset
            </Button>
          </div>

          {status !== "idle" && (
            <div
              className={`flex items-start gap-3 p-4 rounded-md border ${
                status === "success"
                  ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800"
                  : "bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800"
              }`}
              data-testid="status-message"
            >
              {status === "success" ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm">{message}</p>
            </div>
          )}

          <div className="text-sm text-muted-foreground space-y-2 border-t pt-4">
            <p className="font-medium">Examples:</p>
            <div className="space-y-1 ml-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">시드니부동산</Badge>
                <ArrowRight className="w-3 h-3" />
                <Badge variant="outline">부동산</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">멜번한인</Badge>
                <ArrowRight className="w-3 h-3" />
                <Badge variant="outline">멜버른</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">워홀</Badge>
                <ArrowRight className="w-3 h-3" />
                <Badge variant="outline">워킹홀리데이</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
